import { useCallback, useContext, useMemo } from "react";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { ReviewedCart, UserAddress } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";

import {
    impressionDeliveryBanner,
    impressionEnterPnr,
    clickEnterPnr,
    impressionFotCartError,
    impressionUserDetailsBanner,
    clickUpdateDetailsBanner,
} from "@cart-client/analytics/events/Irctc";
import { getCartId, getIrctcIdentifier, getRestaurantId } from "@cart-client/helpers/Cart";
import { getIrctcDeliveryDetailsData, getNonPnrIrctcIdentifier } from "@cart-client/helpers/Irctc";

const IRCTC_FLOW = {
    NON_PNR: "non_pnr",
    PNR: "pnr",
} as const;

export const useIrctcAnalytics = (cartResponse: ReviewedCart | null, selectedAddress?: UserAddress | null) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const restaurantId = useMemo(() => getRestaurantId(cartResponse), [cartResponse]);
    const cartId = useMemo(() => getCartId(cartResponse), [cartResponse]);
    const irctcData = useMemo(() => getIrctcIdentifier(cartResponse), [cartResponse]);
    const isNonPnrFlow = useMemo(() => getNonPnrIrctcIdentifier(cartResponse), [cartResponse]);

    const deliveryDetails = useMemo(
        () => getIrctcDeliveryDetailsData(cartResponse, selectedAddress ?? null),
        [cartResponse, selectedAddress],
    );

    const basePayload = useMemo(
        () => ({
            isIrctc: !isEmpty(irctcData) ? "true" : "false",
            flow: isNonPnrFlow ? IRCTC_FLOW.NON_PNR : IRCTC_FLOW.PNR,
            restaurantId,
            cartId,
        }),
        [irctcData, isNonPnrFlow, restaurantId, cartId],
    );

    const seatNo = useMemo(() => {
        const { coach, berth } = irctcData ?? {};
        return coach && berth ? `${coach}/${berth}` : "";
    }, [irctcData]);

    const contactNo = useMemo(() => irctcData?.mobile_number ?? "", [irctcData]);

    const trackDeliveryBannerImpression = useCallback(() => {
        impressionDeliveryBanner(impressionEvent, {
            ...basePayload,
            stationName: deliveryDetails?.subtitle ?? "",
            deliveryAt: deliveryDetails?.title ?? "",
        });
    }, [impressionEvent, basePayload, deliveryDetails?.subtitle, deliveryDetails?.title]);

    const trackEnterPnrImpression = useCallback(() => {
        impressionEnterPnr(impressionEvent, basePayload);
    }, [impressionEvent, basePayload]);

    const trackEnterPnrClick = useCallback(() => {
        clickEnterPnr(clickEvent, basePayload);
    }, [clickEvent, basePayload]);

    const trackFotCartErrorImpression = useCallback(
        (statusCode?: number | string) => {
            impressionFotCartError(impressionEvent, { ...basePayload, statusCode });
        },
        [impressionEvent, basePayload],
    );

    const pnrNumber = useMemo(() => irctcData?.pnr_number ?? "", [irctcData]);

    const trackUserDetailsBannerImpression = useCallback(() => {
        impressionUserDetailsBanner(impressionEvent, { ...basePayload, seatNo, contactNo, pnrNumber });
    }, [impressionEvent, basePayload, seatNo, contactNo, pnrNumber]);

    const trackUpdateDetailsBannerClick = useCallback(() => {
        clickUpdateDetailsBanner(clickEvent, { ...basePayload, seatNo, contactNo });
    }, [clickEvent, basePayload, seatNo, contactNo]);

    return {
        trackDeliveryBannerImpression,
        trackEnterPnrImpression,
        trackEnterPnrClick,
        trackFotCartErrorImpression,
        trackUserDetailsBannerImpression,
        trackUpdateDetailsBannerClick,
    };
};
